import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../../../lib/api";
import { toast } from "sonner";

export function AccountingPanel() {
  const [preview, setPreview] = useState(null);
  const [forMonth, setForMonth] = useState("");
  const [overrideEmail, setOverrideEmail] = useState("");
  const [sending, setSending] = useState(false);

  const loadPreview = useCallback(async (ym) => {
    try {
      const r = await api.get(`/admin/accounting/preview${ym ? `?for_month=${ym}` : ""}`);
      setPreview(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadPreview(forMonth); }, [forMonth, loadPreview]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/accounting/send-now", {
        override_email: overrideEmail || null,
        for_month: forMonth || null,
      });
      if (r.data.ok) {
        toast.success(`Accounting email sent to ${r.data.sent.join(", ")}`);
      } else {
        toast.error(r.data.reason || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!preview) return null;
  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

  return (
    <div className="bg-[#0F3320] text-[#FBF9F6] rounded-2xl p-6" data-testid="accounting-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Month-end accounting · {preview.month_label}</p>
          <p className="serif text-3xl mt-1" data-testid="accounting-total">{fmt(preview.money_in_total_usd)}</p>
          <p className="text-xs opacity-70 mono mt-1">
            {preview.money_in_count} txn · issuance {fmt(preview.issuance_total_usd)} · topup {fmt(preview.topup_total_usd)}
          </p>
        </div>
        <div className="flex gap-6 mono text-sm">
          <div>
            <p className="text-[10px] tracking-widest opacity-60 uppercase">Service fees</p>
            <p className="mt-1 text-[#8BA888]" data-testid="accounting-service-fees">{fmt(preview.service_fees_usd)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-widest opacity-60 uppercase">Gateway fees</p>
            <p className="mt-1">{fmt(preview.gateway_fees_usd)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-widest opacity-60 uppercase">Refunds</p>
            <p className="mt-1">{preview.refund_notices}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[10px] tracking-widest uppercase opacity-60">Month (YYYY-MM)</label>
          <input
            type="month"
            value={forMonth}
            onChange={(e) => setForMonth(e.target.value)}
            className="block mt-1 px-3 py-2 rounded-lg bg-[#112318] border border-white/10 mono text-sm text-[#FBF9F6]"
            data-testid="accounting-for-month"
          />
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] tracking-widest uppercase opacity-60">Send to (optional override)</label>
          <input
            type="email"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            placeholder="you@example.com (defaults to all admins)"
            className="block mt-1 w-full px-3 py-2 rounded-lg bg-[#112318] border border-white/10 mono text-sm text-[#FBF9F6] placeholder:text-white/30"
            data-testid="accounting-override-email"
          />
        </div>
        <button
          onClick={sendNow}
          disabled={sending}
          className="h-10 px-5 rounded-lg bg-[#8BA888] hover:bg-[#6F8E70] text-[#0A1F16] text-sm font-medium disabled:opacity-50"
          data-testid="accounting-send"
        >
          {sending ? "Sending…" : "Send accounting email now"}
        </button>
      </div>
      <p className="text-[11px] opacity-60 mt-3">
        Auto-fires on the 1st of every month (UTC). Transactions CSV is attached.
      </p>
    </div>
  );
}
